import { computed, inject, Injectable, Injector, signal, TemplateRef } from '@angular/core';
import {
  QuoteCoversData,
  QuoteOverviewLabels,
  QuoteTripDetailsData,
  SuccessScreenContent,
  TripDetailsIcons
} from '@pc/shared';
import { EProcessType, PcButtonIcon, PcIcon } from '@pc/common';
import { DatePipe } from '@angular/common';
import { Translations, TranslationValue } from '../translations.service';
import { DestinationsStore } from '../../../pages/trip-details/where-are-you-travelling/store/destinations.store';
import { TripDatesStore } from '../../../pages/trip-details/when-are-you-travelling/store/trip-dates.store';
import { TravelersStore } from '../../../core/stores/travelers.store';
import { MainStore } from '../../../store/main-store/main-store';
import { ProductsStore } from '../../../pages/covers-page/store/products.store';
import { ProcessTypeStore } from '../../../store/process-type-store/process-type-store';
import { CoverType, PickedCover } from '../../models/cover.interface';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { EnvironmentService } from '../../../environment/environment.service';
import { CoverService } from '../cover.service';

@Injectable({
  providedIn: 'root'
})
export class QuoteOverviewService {
  public translations = inject(Translations);
  public injector = inject(Injector);
  public mainStore = inject(MainStore);

  public basePrice = signal(0);
  public quotePrice = computed(() => this.calculateQuotePrice());
  public totalPremium = computed(() => this.calculateTotalPremium());
  public currencyID = signal(12);
  public currencyISO = signal('AUD');

  public quoteTripDetailsData = computed<QuoteTripDetailsData>(() => this.getQuoteTripDetailsData());
  public quoteCoversData = computed(() => this.getQuoteCoversData());
  public pickedCovers = computed(() => this.getPickedCovers());

  public isQuoteOverviewOpen = signal<boolean>(false);
  public brokerFee = computed(() => this.processTypeStore.brokerFee());
  public showEmailSection = signal<boolean>(this.mainStore.processType() !== EProcessType.AGENTS);

  public successScreenContent = computed(() => this.getSuccessScreenContent());

  public quoteOverviewLabels = this.translations.translateAndMap(
    'QuoteOverview',
    this.getQuoteOverviewLabels,
    this.injector
  );

  public readonly envService = inject(EnvironmentService);
  public privacyPolicyLink = computed(() => this.sanitizeprivacyPolicyLink());
  public readonly GOODS_AND_SERVICES_TAX_RATE = this.envService.environment.gstRate;

  private travelersStore = inject(TravelersStore);
  private destinationsStore = inject(DestinationsStore);
  private tripDatesStore = inject(TripDatesStore);
  private productsStore = inject(ProductsStore);
  private coverService = inject(CoverService);
  private processTypeStore = inject(ProcessTypeStore);
  private summaryTemplateRef: TemplateRef<unknown> | undefined = undefined;
  private sanitizer = inject(DomSanitizer);

  private selectedProduct = computed(() => this.productsStore.selectedProduct());
  private insuredAmount = computed(() => this.getInsuredAmount());
  private excessLevel = computed(() => this.getExcessLevel());

  private additionalCoversPrice = computed(() => this.getCoversTotalPrice());

  public setSummaryTemplateRef(templateRef: TemplateRef<unknown> | undefined): void {
    this.summaryTemplateRef = templateRef;
  }

  public setBasePrice(price: number): void {
    this.basePrice.set(price);
  }

  public setCurrecyId(id: number): void {
    if (!id) return;

    this.currencyID.set(id);
  }

  public setCurrencyISO(iso: string): void {
    if (!iso) return;

    this.currencyISO.set(iso);
  }

  public getSummaryTemplateRef(): TemplateRef<unknown> | undefined {
    return this.summaryTemplateRef;
  }

  public updateQuoteOverviewState(): void {
    this.isQuoteOverviewOpen.update((value) => !value);
  }

  public setBrokerFee(fee: number): void {
    this.processTypeStore.setBrokerFee(fee);
  }

  public getProcessType(): EProcessType {
    return this.mainStore.processType();
  }

  public showEmailQuoteSection(state: boolean): void {
    this.showEmailSection.set(state);
  }

  public getTripDetailsIcons(): TripDetailsIcons {
    return {
      applicant: PcIcon.APPLICANT_HEART,
      calendar: PcButtonIcon.CALENDAR_DETAILED
    };
  }

  private getDateRange(): string {
    const startDate: Date = this.tripDatesStore.dates()?.start || new Date();
    const endDate: Date = this.tripDatesStore.dates()?.end || new Date();
    const datePipe = new DatePipe('en-US');
    const startFormatted = datePipe.transform(startDate, 'd MMM, y');
    const endFormatted = datePipe.transform(endDate, 'd MMM, y');
    return `${startFormatted} - ${endFormatted}`;
  }

  private getQuoteTripDetailsData(): QuoteTripDetailsData {
    return {
      destinations: this.destinationsStore
        .pickedDestinationsData()
        .map((dest) => ({ titleLocal: dest.title, destinationIcon: dest.icon })),
      formattedDateRange: this.getDateRange(),
      travelersAmount: this.travelersStore.allTravelers()?.length || 0
    };
  }

  private calculateQuotePrice(): number {
    return this.basePrice() + this.additionalCoversPrice();
  }

  private calculateTotalPremium(): number {
    const totalPrice = this.quotePrice();
    const brokerFee = this.brokerFee() * this.GOODS_AND_SERVICES_TAX_RATE;

    return totalPrice + brokerFee;
  }

  private getQuoteOverviewLabels(quoteOverview: TranslationValue<'QuoteOverview'>): QuoteOverviewLabels {
    return {
      cardFooterNotice: quoteOverview.medicalQuestionnaireFooterNotice,
      continueButton: quoteOverview.continueButton,
      integratedHeader: quoteOverview.policyContentsHeader,
      itemsCardBottom: quoteOverview.currencyNotice,
      itemsCardNotice: quoteOverview.discountNotice,
      itemTooltip: quoteOverview.SelectedCovers?.BaseCoverageNotice,
      yourQuote: quoteOverview.yourQuote,
      priceMessage: quoteOverview.includedLabel,
      total: quoteOverview.totalLabel,
      totalPriceDescription: quoteOverview.paymentAmountDescription,
      travelersAmount: {
        multiple: quoteOverview.travelersCountLabel?.multiple,
        single: quoteOverview.travelersCountLabel?.single
      },
      destinationAltPrefix: quoteOverview.destinationAltPrefix,
      destinationsToggleOptions: {
        less: quoteOverview.destinationsToggleOptions?.less,
        more: quoteOverview.destinationsToggleOptions?.more
      },
      selectedCoversTexts: {
        BasicCovers: quoteOverview.SelectedCovers?.BasicCovers,
        OptionalCovers: quoteOverview.SelectedCovers?.OptionalCovers,
        ActivityCovers: quoteOverview.SelectedCovers?.ActivityCovers,
        YourCover: quoteOverview.SelectedCovers?.YourCover,
        AdditionalCovers: quoteOverview.SelectedCovers?.AdditionalCovers,
        BasePackage: quoteOverview.SelectedCovers?.BasePackage,
        YearlyBasePackage: quoteOverview.SelectedCovers?.YearlyBasePackage,
        BaseCoverageNotice: quoteOverview.SelectedCovers?.BaseCoverageNotice
      },
      brokerFeeTexts: {
        brokerFeeTitle: quoteOverview.brokerFee?.brokerFeeTitle,
        brokerFeeSubtitle: quoteOverview.brokerFee?.brokerFeeSubtitle,
        brokerFeePlaceholder: quoteOverview.brokerFee?.brokerFeePlaceholder,
        brokerFeeButton: quoteOverview.brokerFee?.brokerFeeButton
      },
      emailQuoteTexts: {
        emailQuoteTitle: quoteOverview.emailQuote?.emailQuoteTitle,
        emailQuoteInputLabel: quoteOverview.emailQuote?.emailQuoteInputLabel,
        emailQuoteAlertTitle: quoteOverview.emailQuote?.emailQuoteAlertTitle,
        emailQuoteAlertSubTitle: quoteOverview.emailQuote?.emailQuoteAlertSubTitle,
        emailQuoteButton: quoteOverview.emailQuote?.emailQuoteButton
      }
    };
  }

  private getPickedCovers(): PickedCover[] {
    if (!this.processTypeStore.isFullFlowProcess()) {
      return this.getPickedCoversFromQuote();
    }

    return this.getPickedCoversFromStore();
  }

  private getQuoteCoversData(): QuoteCoversData[] {
    const coversData: QuoteCoversData[] = [
      {
        label: this.quoteOverviewLabels().selectedCoversTexts.YourCover,
        covers: [
          { title: `Comprehensive Cover`, price: 0 },
          { title: `Cancellation Cover Limit up to ${this.insuredAmount()}`, price: 0 },
          { title: `Excess Amount of ${this.excessLevel()}`, price: 0 }
        ]
      }
    ];

    const optional = this.buildCoversViewElement(
      CoverType.optional,
      this.quoteOverviewLabels().selectedCoversTexts.OptionalCovers
    );
    const activity = this.buildCoversViewElement(
      CoverType.activity,
      this.quoteOverviewLabels().selectedCoversTexts.ActivityCovers
    );

    if (optional.covers.length) coversData.push(optional);
    if (activity.covers.length) coversData.push(activity);

    return coversData;
  }

  private buildCoversViewElement(coverType: CoverType, label: string): QuoteCoversData {
    const covers = this.pickedCovers()
      .filter((cover) => cover.type === coverType)
      .map((cover) => ({
        title: cover.name,
        price: cover.price
      }));

    return {
      label: label,
      covers: covers
    };
  }

  private getPickedCoversFromStore(): PickedCover[] {
    const covers: Record<number, PickedCover> = {};

    this.travelersStore.allTravelers().forEach((traveler) => {
      const coverIds =
        traveler.productCover?.find((product) => product.productID === this.selectedProduct()?.policyGroupID)
          ?.coversIds || [];
      coverIds.forEach((id) => {
        const coverData =
          this.selectedProduct()?.covers.find((cover) => cover.id === id) ??
          this.coverService.getParentCoverFromAddedCoverLevel(id);
        if (!coverData) return;

        const cover = {
          id: id,
          name: coverData.name,
          price: coverData.travelers?.find((t) => t.ParticipentNum === traveler.participantNum)?.Price || 0,
          type: coverData.coverType,
          websiteOrder: coverData.webSiteOrder,
          quantity: 1
        };

        if (covers[id]) {
          covers[id].price += cover.price;
          covers[id].quantity = (covers[id].quantity || 0) + cover.quantity;
        } else {
          covers[id] = cover;
        }
      });
    });

    return Object.values(covers).sort((a, b) => a.websiteOrder - b.websiteOrder);
  }

  private getInsuredAmount(): string {
    const product = this.productsStore
      .products()
      .find((product) => product.policyGroupID === this.productsStore.selectedProductId());

    if (!product) {
      return this.formatCurrency(this.processTypeStore.quoteUrlData()?.InsuredAmount);
    }
    return this.formatCurrency(product.insuredAmountData.find((item) => item.IsSelected)?.InsuredAmountValue);
  }

  private getExcessLevel(): string {
    const product = this.productsStore
      .products()
      .find((product) => product.policyGroupID === this.productsStore.selectedProductId());

    if (!product) {
      return this.formatCurrency(this.processTypeStore.quoteUrlData()?.SelectedExcessLevel);
    }

    return this.formatCurrency(product?.excessLevels.find((item) => item.IsSelected)?.ExcessLevelValue);
  }

  private getCoversTotalPrice(): number {
    let totalPrice = 0;

    this.pickedCovers().forEach((cover) => {
      totalPrice += cover.price;
    });

    return totalPrice;
  }

  private getPickedCoversFromQuote(): PickedCover[] {
    const covers = this.processTypeStore.quoteUrlData()?.quoteDetailsRiders || [];

    return covers.map((rider) => ({
      id: rider.BenefitId,
      name: rider.BenefitName,
      price: rider.Price,
      type: rider.CoverType,
      quantity: 0
    })) as PickedCover[];
  }

  private getSuccessScreenContent(): SuccessScreenContent {
    return {
      policyNumber: this.mainStore.makePolicyResult()?.TravelPolicyNumber || '',
      labels: {
        confirmationTitle: this.translations.SuccessScreen.confirmationTitle,
        confirmationSubtitle: this.translations.SuccessScreen.confirmationSubtitle,
        policyNumberLabel: this.translations.SuccessScreen.policyNumberLabel,
        summaryOfCover: this.translations.SuccessScreen.summaryOfCover,
        footerNotice: this.translations.SuccessScreen.footerNotice
      },
      quoteTripDetailsData: this.quoteTripDetailsData(),
      quoteCoversData: this.quoteCoversData(),
      quoteOverviewLabels: this.quoteOverviewLabels()
    };
  }

  private sanitizeprivacyPolicyLink(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(
      this.translations.instant(this.translations.QuoteOverview.emailQuote.emailQuoteSubtitle, {
        url: this.envService.environment.links.privacyPolicyLink
      })
    );
  }

  private formatCurrency(value: number | undefined): string {
    return `$${value?.toLocaleString('en-US') || 0}`;
  }
}
